import React from "react";
import { I18n } from "@aws-amplify/core";
import { get, forEach, size, filter, isEmpty, join, fill } from "lodash";
import FileSaver from "file-saver";

import { formatDate } from "./date";
import { getAttributeName } from "./attributes";

export const displayData = (data) => {
  if (data || data === 0) {
    return data;
  }
  return <span className="text-87">-</span>;
};

export const displayDataCsv = (data) => {
  if (data || data === 0) {
    return data;
  }
  return "-";
};

export const displayBoolean = (bool) => {
  if (bool === true) {
    return I18n.get("profiles_yes");
  } else if (bool === false) {
    return I18n.get("profiles_no");
  }
  return "-";
};

export const displayPercentage = (percentage) => {
  if (
    (percentage || percentage === 0) &&
    !Number.isNaN(percentage) &&
    !isNaN(percentage)
  ) {
    if (percentage <= 1) {
      percentage = Math.round(percentage * 100);
    }
  } else {
    percentage = "-";
  }
  return `(${percentage} %)`;
};

export const calculatePercentage = (value, total) => {
  if (value === 0) {
    return 0;
  }
  return Math.round((value / total) * 100);
};

export const exportTableToCsv = (
  title,
  cols,
  data,
  dataFunctions,
  I18nget,
  dates,
  headers
) => {
  const columns = filter(cols, (c) => c.id || c.name || c.iconName);
  const separator = ",";
  let csv = "\uFEFF";

  if (title) {
    csv += `"${title}"\r\n`;
  }

  if (!isEmpty(dates) && get(dates, ["from"]) !== get(dates, ["to"])) {
    csv += `"${I18nget("common_from")}"${separator}"${formatDate(
      get(dates, ["from"])
    )}"\r\n`;
    csv += `"${I18nget("common_to")}"${separator}"${formatDate(
      get(dates, ["to"])
    )}"\r\n`;
  }

  if (!isEmpty(headers)) {
    forEach(headers, (h, index) => {
      csv += `"${I18nget(h.name)}"${join(
        fill(new Array(h.spanNb - 1), separator),
        ""
      )}${index === size(headers) - 1 ? "\r\n" : separator}`;
    });
  }

  // Headers row
  forEach(columns, (col, index) => {
    csv += `"${col.id
      ? getAttributeName(col.id)
      : col.name2
        ? col.name2
        : col.name
          ? I18nget(col.name)
          : col.iconName
            ? I18nget(col.iconName)
            : ""
      }"${index === size(columns) - 1 ? "\r\n" : separator}`;
  });

  // Data rows
  forEach(data, (item) => {
    forEach(columns, (col, index) => {
      if (dataFunctions[index]) {
        csv += `"${dataFunctions[index](item)}"${index === size(columns) - 1 ? "\r\n" : separator
          }`;
      }
    });
  });

  const csvData = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  FileSaver.saveAs(csvData, `${title || data}.csv`);
};

export const exportTrainToCsv = (
  title,
  cols,
  railcarData,
  trainId,
  trainYardEventType,
  trainComments,
  I18nget,
  dates,
) => {
  const columns = filter(cols, (c) => c.id || c.name || c.iconName);
  const separator = ",";
  let csv = "\uFEFF";

  if (title) {
    csv += `"${title}"\r\n`;
  }

  if (!isEmpty(dates) && get(dates, ["from"]) !== get(dates, ["to"])) {
    csv += `"${I18nget("common_from")}"${separator}"${formatDate(
      get(dates, ["from"])
    )}"\r\n`;
    csv += `"${I18nget("common_to")}"${separator}"${formatDate(
      get(dates, ["to"])
    )}"\r\n`;
  }

  forEach(columns, (col, index) => {
    csv += `"${col.name}"${index === size(columns) - 1 ? "\r\n" : separator}`;
  });

  // Data rows
  const returnRailcarName = id => {
    if (id === 171) {
      return "Locomotive"
    } else if (id === 172) {
      return "Maintenance"
    } else if (id === 173) {
      return "AutoRack"
    } else if (id === 174) {
      return "BoxCar"
    } else if (id === 175) {
      return "RefrigeratedBoxCar"
    } else if (id === 176) {
      return "CenterBeam"
    } else if (id === 177) {
      return "ClosedHopper"
    } else if (id === 178) {
      return "OpenHopper"
    } else if (id === 179) {
      return "Gondola"
    } else if (id === 180) {
      return "Tanker"
    } else if (id === 181) {
      return "WellCar"
    } else if (id === 182) {
      return "Flatcar"
    } else if (id === 183) {
      return "Passenger"
    } else if (id === 185) {
      return "CoilCar"
    }
  }

  forEach(railcarData, (item) => {

    forEach(columns, (col, index) => {

      if (col.name === "Train ID") {
        csv += `"${trainId ? trainId : "-"}"${index === size(columns) - 1 ? "\r\n" : separator
          }`;
      } else if (col.name === "Date & Time") {
        csv += `"${formatDate(item.events.filter(e => e.type.id === 181)[0].attributes.filter(a => a.id === 13)[0].value)}"${index === size(columns) - 1 ? "\r\n" : separator
          }`;
      } else if (col.name === "Profile Name") {
        csv += `"${item.profile && item.profile.id.substring(0, 4).localeCompare("0000") !== 0 ? item.profile?.name : "?"}"${index === size(columns) - 1 ? "\r\n" : separator
          }`;
      } else if (col.name === "Sequence No.") {
        csv += `"${item.attributes.filter(a => a.id === 121)[0].value}"${index === size(columns) - 1 ? "\r\n" : separator
          }`;
      } else if (col.name === "Profile Type") {
        csv += `"${item.profile.profile_type_name_en}"${index === size(columns) - 1 ? "\r\n" : separator
          }`;
      } else if (col.name === "Railcar Type") {
        csv += `"${returnRailcarName(item.attributes.filter(a => a.id === 283)[0].value)}"${index === size(columns) - 1 ? "\r\n" : separator
          }`;
      } else if (col.name === "Yard Event Type") {
        csv += `"${trainYardEventType}"${index === size(columns) - 1 ? "\r\n" : separator
          }`;
      } else if (col.name === "Comments") {
        csv += `"${trainComments}"${index === size(columns) - 1 ? "\r\n" : separator
          }`;
      } else {
        csv += `"${"-"}"${index === size(columns) - 1 ? "\r\n" : separator
          }`;
      }

    });

    // CARGO EXTRACTION
    if (item.event_groups && item.event_groups[0]) {
      forEach(item.event_groups, (cargo) => {

        forEach(columns, (col, index) => {

          if (col.name === "Train ID") {
            csv += `"${trainId ? trainId : "-"}"${index === size(columns) - 1 ? "\r\n" : separator
              }`;
          } else if (col.name === "Date & Time") {
            csv += `"${formatDate(cargo.created_on)}"${index === size(columns) - 1 ? "\r\n" : separator
              }`;
          } else if (col.name === "Profile Name") {
            csv += `"${cargo.profile && cargo.profile.id.substring(0, 4).localeCompare("0000") !== 0 ? cargo.profile?.name : "?"}"${index === size(columns) - 1 ? "\r\n" : separator
              }`;
          } else if (col.name === "Sequence No.") {
            csv += `"${item.attributes.filter(a => a.id === 121)[0].value}"${index === size(columns) - 1 ? "\r\n" : separator
              }`;
          } else if (col.name === "Container Position") {
            csv += `"${cargo.attributes.filter(a => a.id === 285)[0].value}"${index === size(columns) - 1 ? "\r\n" : separator
              }`;
          } else if (col.name === "Profile Type") {
            csv += `"${cargo.type.name_en}"${index === size(columns) - 1 ? "\r\n" : separator
              }`;
          } else if (col.name === "Railcar Type") {
            csv += `"${"-"}"${index === size(columns) - 1 ? "\r\n" : separator
              }`;
          } else {
            csv += `"${"-"}"${index === size(columns) - 1 ? "\r\n" : separator
              }`;
          }
        });
      })
    }




  });

  const csvData = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  FileSaver.saveAs(csvData, `${title || railcarData}.csv`);

};

export const downloadFile = async (rowData, fileName) => {
  let blob = await rowData.blob();
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);

  // Append to html link element page
  document.body.appendChild(link);

  // Start download
  link.click();

  // Clean up and remove the link
  link.parentNode.removeChild(link);
};

export const displayColon = (language) => {
  return language === "fr" ? " :" : ":";
};

// Multi level of children
export const calculateChildrenCount = (item, field) => {
  if (get(item, [field])) {
    let count = size(get(item, [field]));
    forEach(get(item, [field]), (child) => {
      count += calculateChildrenCount(child, field);
    });
    return count;
  }
  return 0;
};
